import React from 'react';
import { ChevronDown, Command } from 'lucide-react';
import { theme } from '../../styles/theme';
import { SidebarItem } from './index';

interface SidebarProps {
  items: SidebarItem[];
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  selectedSection: string;
  onSectionChange: (section: string) => void;
  expandedMenus: string[];
  onExpandedMenusChange: (menus: string[]) => void;
  darkMode: boolean;
  onCommandPaletteOpen: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  items,
  expanded,
  onExpandedChange: _onExpandedChange, // Not used - sidebar permanently expanded
  selectedSection,
  onSectionChange,
  expandedMenus,
  onExpandedMenusChange,
  darkMode,
  onCommandPaletteOpen,
}) => {
  const toggleSubmenu = (itemId: string) => {
    onExpandedMenusChange(
      expandedMenus.includes(itemId)
        ? expandedMenus.filter(id => id !== itemId)
        : [...expandedMenus, itemId]
    );
  };

  const handleItemClick = (itemId: string, hasChildren: boolean) => {
    if (hasChildren) {
      toggleSubmenu(itemId);
    } else {
      onSectionChange(itemId);
    }
  };

  const isItemActive = (itemId: string) => {
    return selectedSection === itemId || selectedSection.startsWith(itemId + '-');
  };

  return (
    <div 
      className={`${expanded ? 'w-64' : 'w-16'} transition-all duration-300 flex flex-col relative`}
      style={{
        backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
        borderRight: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
      }}
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-4" style={{
        borderBottom: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
      }}>
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold" style={{
            background: `linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.secondary.main} 100%)`,
          }}>
            H
          </div>
          {expanded && (
            <span className="font-semibold" style={{ 
              color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
            }}>
              Hinata PAL
            </span>
          )}
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = isItemActive(item.id);
          const isExpanded = expandedMenus.includes(item.id);
          const hasChildren = !!(item.children && item.children.length > 0);
          
          return (
            <div key={item.id}>
              <button
                onClick={() => handleItemClick(item.id, hasChildren)}
                className={`w-full flex items-center px-4 py-3 relative transition-colors`}
                style={{
                  backgroundColor: isActive 
                    ? darkMode ? 'rgba(250, 175, 144, 0.1)' : 'rgba(250, 175, 144, 0.15)'
                    : 'transparent',
                  color: isActive 
                    ? theme.colors.primary.dark
                    : darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                  borderLeft: isActive ? `2px solid ${theme.colors.primary.main}` : '2px solid transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = darkMode 
                      ? 'rgba(250, 175, 144, 0.05)' 
                      : 'rgba(250, 175, 144, 0.08)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {expanded && (
                  <>
                    <span className="ml-3 flex-1 text-left">{item.label}</span>
                    {hasChildren && (
                      <ChevronDown 
                        className={`w-4 h-4 transition-transform ${
                          isExpanded ? 'transform rotate-180' : ''
                        }`} 
                      />
                    )}
                    {item.badge && !hasChildren && (
                      <span className="ml-2 px-2 py-0.5 text-xs rounded-full text-white" style={{
                        backgroundColor: item.badge === '!' 
                          ? theme.colors.status.error 
                          : theme.colors.secondary.main
                      }}>
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
                {!expanded && item.badge && (
                  <div className="absolute top-2 right-2 w-2 h-2 rounded-full" style={{
                    backgroundColor: item.badge === '!' ? theme.colors.status.error : theme.colors.secondary.main
                  }}></div>
                )}
              </button>
              
              {/* Submenu Items */}
              {hasChildren && expanded && (
                <div className={`overflow-hidden transition-all duration-300 ${
                  isExpanded ? 'max-h-96' : 'max-h-0'
                }`}>
                  {item.children!.map((child) => {
                    const isChildActive = selectedSection === child.id;
                    const ChildIcon = child.icon;
                    
                    return (
                      <button
                        key={child.id}
                        onClick={() => onSectionChange(child.id)}
                        className="w-full flex items-center pl-12 pr-4 py-2 relative transition-colors"
                        style={{
                          backgroundColor: isChildActive 
                            ? darkMode ? 'rgba(107, 155, 209, 0.1)' : 'rgba(107, 155, 209, 0.1)'
                            : 'transparent',
                          color: isChildActive
                            ? theme.colors.secondary.dark
                            : darkMode ? '#999' : theme.colors.text.secondary,
                        }}
                        onMouseEnter={(e) => {
                          if (!isChildActive) {
                            e.currentTarget.style.backgroundColor = darkMode 
                              ? 'rgba(107, 155, 209, 0.05)' 
                              : 'rgba(107, 155, 209, 0.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isChildActive) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {ChildIcon && (
                          <ChildIcon className="w-4 h-4 mr-3 flex-shrink-0" />
                        )}
                        {!ChildIcon && (
                          <div className="w-1.5 h-1.5 rounded-full mr-3" style={{
                            backgroundColor: isChildActive 
                              ? theme.colors.secondary.main 
                              : darkMode ? '#666' : '#ccc'
                          }} />
                        )}
                        <span className="flex-1 text-left text-sm">{child.label}</span>
                        {child.status && (
                          <div className="w-2 h-2 rounded-full ml-2" style={{
                            backgroundColor: child.status === 'healthy' ? theme.colors.status.success :
                                           child.status === 'warning' ? theme.colors.status.warning :
                                           theme.colors.status.error
                          }} />
                        )}
                        {child.badge && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full text-white" style={{
                            backgroundColor: child.badge === '!' 
                              ? theme.colors.status.error
                              : child.badge === 'LIVE'
                                ? theme.colors.status.success
                                : theme.colors.secondary.light
                          }}>
                            {child.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Command Palette Trigger */}
      <div className="p-4" style={{
        borderTop: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
      }}>
        <button
          onClick={onCommandPaletteOpen}
          className="w-full flex items-center justify-center px-3 py-2 rounded-lg transition-colors"
          style={{
            backgroundColor: darkMode ? 'rgba(107, 155, 209, 0.1)' : theme.colors.secondary.light + '20',
            color: theme.colors.secondary.main,
          }}
        >
          <Command className="w-4 h-4" />
          {expanded && (
            <span className="ml-2 text-sm">⌘K</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;